import {useCreatePaymentWay} from '@rocket-ui/lk-api';
import {
	activateEmploymentWithCurrent,
	buildPaymentWayRequest,
	resolveEmploymentRequestProvider,
	EMPTY_PAYMENT_DETAILS,
	getInitialPaymentSubStep,
	isPaymentDetailsStepValid,
	RegistrationPaymentMethod,
	RegistrationStep,
	requiresPaymentDetailsStep,
} from 'features/registration';
import type {RegistrationWizardState} from './useRegistrationWizardState';

type Params = {
	state: RegistrationWizardState;
	hasExistingPaymentWays: boolean;
	completeStatusConnection: () => void;
};

/** Подшаги ввода платёжных реквизитов и сохранение способа выплат. */
export const useRegistrationPaymentFlow = ({
	state,
	hasExistingPaymentWays,
	completeStatusConnection,
}: Params) => {
	const {mutateAsync: createPaymentWay} = useCreatePaymentWay();

	const {
		paymentDetails,
		paymentSubStep,
		pendingPaymentMethod,
		resetPaymentFlow,
		setIsSubmitting,
		setMyTaxLoadErrorRetry,
		setPaymentDetails,
		setPaymentSubStep,
		setPendingPaymentMethod,
		selfEmploymentPartner,
		setStep,
		workStatus,
	} = state;

	const handlePaymentMethodSelect = (method: RegistrationPaymentMethod) => {
		if (!requiresPaymentDetailsStep(method)) {
			resetPaymentFlow();
			completeStatusConnection();
			return;
		}

		setPendingPaymentMethod(method);
		setPaymentSubStep(getInitialPaymentSubStep(method));
		setPaymentDetails(EMPTY_PAYMENT_DETAILS);
	};

	const advancePaymentSubStep = () => {
		if (!pendingPaymentMethod) {
			return;
		}

		switch (paymentSubStep) {
			case 'sbp_phone':
				setPaymentSubStep('sbp_bank');
				return;
			case 'sbp_bank':
				setPaymentSubStep('sbp_review');
				return;
			case 'card_number':
				setPaymentSubStep('card_review');
				return;
			case 'account_bik':
				setPaymentSubStep('account_number');
				return;
			case 'account_number':
				setPaymentSubStep('account_review');
				return;
			default:
				return;
		}
	};

	const completePaymentFlow = async () => {
		if (!pendingPaymentMethod || !workStatus) {
			return;
		}

		const paymentRequest = buildPaymentWayRequest(pendingPaymentMethod, {
			...paymentDetails,
			isPrimary: hasExistingPaymentWays ? paymentDetails.isPrimary : true,
		});

		if (!paymentRequest) {
			return;
		}

		setIsSubmitting(true);

		try {
			await createPaymentWay(paymentRequest);
			await activateEmploymentWithCurrent(workStatus, {
				provider: resolveEmploymentRequestProvider(workStatus, selfEmploymentPartner),
			});
			// Сначала уходим с PaymentMethod, иначе resetPaymentFlow → select снова автооткрывает БИК.
			setStep(RegistrationStep.MyTaxSuccess);
			resetPaymentFlow();
		} catch {
			setMyTaxLoadErrorRetry('activate');
			setStep(RegistrationStep.MyTaxLoadError);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handlePaymentContinue = () => {
		if (!pendingPaymentMethod || !isPaymentDetailsStepValid(pendingPaymentMethod, paymentSubStep, paymentDetails, workStatus)) {
			return;
		}

		if (paymentSubStep === 'account_number') {
			setPaymentDetails((current) => ({...current, accountError: null}));
			advancePaymentSubStep();
			return;
		}

		if (paymentSubStep === 'sbp_review' || paymentSubStep === 'account_review' || paymentSubStep === 'card_review') {
			void completePaymentFlow();
			return;
		}

		advancePaymentSubStep();
	};

	return {
		handlePaymentContinue,
		handlePaymentMethodSelect,
	};
};

export type RegistrationPaymentFlow = ReturnType<typeof useRegistrationPaymentFlow>;
